const locks = new Set<symbol>();
const listeners = new Set<() => void>();
const reservationListeners = new Set<() => void>();
let reservation: { readonly expiresAt: number; readonly invalidate: () => void } | undefined;

export interface PwaUpdateReservation {
  readonly valid: () => boolean;
  readonly release: () => void;
}

/** Hold for an active scanner, pending/sending scans, or an unsaved operation.
 * Only a UI update guard: it grants no offline identity or authorization. */
export function acquirePwaUpdateLock(): () => void {
  // A newly started background operation revokes readiness before its caller
  // continues. The short interaction barrier remains until abort/activation.
  reservation?.invalidate();
  const lock = Symbol('pwa-operation');
  locks.add(lock);
  for (const listener of listeners) listener();
  return () => {
    if (!locks.delete(lock)) return;
    for (const listener of listeners) listener();
  };
}

export function subscribePwaUpdateLocks(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function hasPwaUpdateLock(): boolean { return locks.size > 0; }

export function isPwaUpdateReserved(): boolean { return reservation !== undefined; }
export function subscribePwaUpdateReservations(listener: () => void): () => void {
  reservationListeners.add(listener);
  return () => { reservationListeners.delete(listener); };
}

/** A bounded user-interaction reservation, not a business/authorization lock.
 * Existing operations must already hold their operation lock. Background
 * operations acquiring one revoke readiness; no caller is made to throw. */
export function reservePwaUpdate(expiresAt: number, onInvalidated: () => void): PwaUpdateReservation | undefined {
  if (reservation || !canApplyPwaUpdate() || !Number.isFinite(expiresAt)
    || expiresAt <= Date.now() || expiresAt > Date.now() + 10_000) return undefined;
  let invalidated = false;
  let released = false;
  const current = { expiresAt, invalidate: () => {
    if (invalidated || released) return;
    invalidated = true;
    onInvalidated();
  } };
  const interactionTypes = ['pointerdown', 'mousedown', 'touchstart', 'click', 'keydown', 'beforeinput', 'input', 'change', 'submit', 'drop', 'paste', 'cut'] as const;
  const preventNewInteraction = (event: Event) => { event.preventDefault(); event.stopImmediatePropagation(); };
  const invalidate = () => current.invalidate();
  const release = () => {
    if (released) return;
    released = true;
    clearTimeout(timer);
    for (const type of interactionTypes) window.removeEventListener(type, preventNewInteraction, true);
    window.removeEventListener('offline', invalidate);
    window.removeEventListener('event:session-outbox-changed', invalidate);
    if (reservation === current) reservation = undefined;
    for (const listener of reservationListeners) listener();
  };
  reservation = current;
  for (const type of interactionTypes) window.addEventListener(type, preventNewInteraction, { capture: true, passive: false });
  window.addEventListener('offline', invalidate);
  window.addEventListener('event:session-outbox-changed', invalidate);
  const timer = setTimeout(release, expiresAt - Date.now());
  for (const listener of reservationListeners) listener();
  return { release, valid: () => !released && !invalidated && reservation === current && Date.now() < expiresAt && canApplyPwaUpdate() };
}

export function canApplyPwaUpdate(): boolean {
  if (!navigator.onLine || hasPwaUpdateLock()) return false;
  // Defense in depth if a scanner integration forgets its explicit lock.
  return ![...document.querySelectorAll('video')].some((video) => {
    const stream = video.srcObject;
    return typeof MediaStream !== 'undefined' && stream instanceof MediaStream
      && stream.getVideoTracks().some((track) => track.readyState === 'live');
  });
}
